import React, { useMemo, useState } from 'react';

import DayCard from './DayCard';
import TipsList from './TipsList';
import { recomputePlanDurations } from './recomputeDurations';

// Graphite + Lime (2026-10-27). Presentation only: every figure comes from the stored plan row — a value
// the row does not carry is hidden, never shown as "—" or 0.

// Race day = a goal / tune-up race title at an official race distance (backend race_distances.is_race_day).
const RACE_DAY_PREFIXES = ['מרוץ מטרה', 'מרוץ אימון'];
const OFFICIAL_RACE_KM = [5, 10, 21.1, 42.2];
export function isRaceDay(day) {
  if (!day || day.is_rest) return false;
  const kind = String(day.type || '');
  if (!RACE_DAY_PREFIXES.some(p => kind.startsWith(p))) return false;
  const km = Number(day.km);
  return Number.isFinite(km) && OFFICIAL_RACE_KM.some(k => Math.abs(km - k) <= 0.06);
}

// Long run = a day titled "ריצה ארוכה" only (the Home model's rule) — never "the longest day".
const isLongRun = (day) => !day.is_rest && String(day.type || '').includes('ריצה ארוכה');

function computeStats(plan) {
  const workoutDays = (plan?.days || []).filter(d => !d.is_rest);
  const longKm = workoutDays.filter(isLongRun).reduce((mx, d) => Math.max(mx, d.km || 0), 0);
  return {
    workoutCount: workoutDays.length,
    raceCount: workoutDays.filter(isRaceDay).length,
    longRunKm: longKm > 0 ? longKm : null,
  };
}

function weekTotalKm(wkPlan) {
  if (wkPlan.total_km > 0) return wkPlan.total_km;
  const sum = (Array.isArray(wkPlan.days) ? wkPlan.days : []).reduce((a, d) => a + (d.km || 0), 0);
  return sum > 0 ? Math.round(sum * 10) / 10 : null;
}

function formatShort(iso) {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

function formatPaceRange(pace) {
  if (!pace) return '';
  const isPlaceholder = v => !v || v === '0:00' || v === '00:00';
  if (!isPlaceholder(pace.min) && !isPlaceholder(pace.max))
    return `${pace.min}–${pace.max}`;
  if (pace.label) return pace.label;
  return '';
}

const localIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ── DayRow — today: 2px lime start edge; race day: lime edge; rest days muted ──
function DayRow({ day, dayDate, isToday, isOpen, onExpand }) {
  const race     = isRaceDay(day);
  const paceStr  = formatPaceRange(day.pace);
  const zoneStr  = day.target_zone || '';
  const subtitle = [zoneStr, paceStr && `${paceStr} לק״מ`].filter(Boolean).join(' · ');
  const edge = (isToday || race) ? { borderInlineStartWidth: 2, borderInlineStartColor: 'var(--accent)' } : undefined;

  if (day.is_rest) {
    return (
      <div className="flex items-center gap-3 px-4 min-h-[48px] rounded-[16px] border border-border" style={edge}
           aria-current={isToday ? 'date' : undefined}>
        <div className="min-w-[58px]">
          <div className={`font-semibold text-[14px] whitespace-nowrap ${isToday ? 'text-accent' : 'text-text-muted'}`}>{day.day_name}</div>
          {dayDate && <div className="text-[12px] text-text-muted">{isToday ? 'היום' : dayDate}</div>}
        </div>
        <span className="text-[15px] text-text-muted">מנוחה</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onExpand?.(day)}
      aria-expanded={isOpen}
      aria-current={isToday ? 'date' : undefined}
      className="w-full flex items-center gap-3 p-4 min-h-[64px] rounded-[16px] bg-surface border border-border text-start"
      style={edge}
    >
      <div className="min-w-[58px] shrink-0">
        <div className={`font-bold text-[14px] whitespace-nowrap ${isToday ? 'text-accent' : 'text-text'}`}>{day.day_name}</div>
        {dayDate && <div className="text-[12px] text-text-muted">{isToday ? 'היום' : dayDate}</div>}
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-bold text-[16px] text-text truncate">{day.type || 'אימון'}</div>
        {subtitle && <div className="text-[13px] text-text-muted mt-0.5 truncate">{subtitle}</div>}
      </div>

      {day.km > 0 && (
        <div className="shrink-0 text-center">
          <div className={`num-display text-[26px] tabular-nums ${race ? 'text-accent' : 'text-text'}`} style={{ lineHeight: 1 }}>{day.km}</div>
          <div className="text-[12px] text-text-muted">ק״מ</div>
        </div>
      )}
    </button>
  );
}

// ── WeekTab — "שבוע 1".."שבוע 4" with the week's planned km; selected = lime + underline ──
function WeekTab({ weekRow, weekIdx, isSelected, onClick }) {
  const wkPlan = weekRow.workouts_json || {};
  const days = Array.isArray(wkPlan.days) ? wkPlan.days : [];
  const isRestWeek = !weekRow._missing && days.length > 0 && days.every(d => d.is_rest);
  const totalKm = weekRow._missing ? null : weekTotalKm(wkPlan);
  const available = !weekRow._missing && (totalKm != null || isRestWeek || wkPlan.phase === 'recovery');

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isSelected}
      disabled={!available}
      onClick={onClick}
      className={`relative flex-1 min-w-0 min-h-[56px] flex flex-col items-center justify-center gap-0.5 pb-2 ${
        isSelected ? 'text-accent' : available ? 'text-text-body' : 'text-text-muted opacity-60'
      }`}
    >
      <span className={`text-[13px] ${isSelected ? 'font-bold' : 'font-medium'}`}>שבוע {weekIdx + 1}</span>
      {available ? (
        <span className="text-[13px] tabular-nums">
          {isRestWeek ? 'מנוחה' : totalKm != null ? `${totalKm} ק״מ` : 'התאוששות'}
        </span>
      ) : (
        <span className="text-[12px]">טרם נוצר</span>
      )}
      {isSelected && <span aria-hidden className="absolute bottom-0 h-[2px] w-6 bg-accent" />}
    </button>
  );
}

// ── Main renderer ─────────────────────────────────────────────────────
export default function WeeklyPlanRenderer({
  weeks,
  plan: planProp,
  initialWeekIdx = 0,
  onWeekChange,
  onClose,
}) {
  const [selectedWeekIdx, setSelectedWeekIdx] = useState(initialWeekIdx);
  const [expandedDay, setExpandedDay]         = useState(null);

  const weeksList = useMemo(() => {
    if (weeks?.length) return weeks;
    if (planProp) return [{ workouts_json: planProp, _label: 'current' }];
    return [];
  }, [weeks, planProp]);

  const selectedRow = weeksList[Math.min(selectedWeekIdx, weeksList.length - 1)];

  const plan = useMemo(() => {
    const raw = selectedRow?.workouts_json;
    if (!raw || !Array.isArray(raw.days)) return null;
    return recomputePlanDurations(raw);
  }, [selectedRow]);

  const handleWeekSelect = (idx) => {
    setSelectedWeekIdx(idx);
    setExpandedDay(null);
    onWeekChange?.(idx);
  };

  const tabs = weeksList.length > 1 && (
    <div role="tablist" aria-label="שבועות" className="flex border-b border-border">
      {weeksList.map((wk, i) => (
        <WeekTab key={i} weekRow={wk} weekIdx={i} isSelected={i === selectedWeekIdx} onClick={() => handleWeekSelect(i)} />
      ))}
    </div>
  );

  if (!weeksList.length || weeksList.every(w => w._missing))
    return <p className="text-center text-text-muted text-sm py-6">אין תכנית להצגה.</p>;
  if (selectedRow?._missing)
    return (
      <div className="flex flex-col gap-4">
        {tabs}
        <p className="text-center text-text-muted text-sm py-6">השבוע הזה טרם נוצר — הוא ייבנה אוטומטית בהמשך.</p>
      </div>
    );
  if (!plan)
    return <p className="text-center text-text-body text-sm py-6">התכנית לא נטענה כראוי — נסה לרענן.</p>;

  const stats      = computeStats(plan);
  const totalKm    = weekTotalKm(plan);
  const dateRange  = plan.week_start && plan.week_end ? `${formatShort(plan.week_start)} – ${formatShort(plan.week_end)}` : null;
  const ctx        = plan.context || {};
  const daysToRace = ctx.days_until_goal;
  const raceName   = ctx.goal_race?.name;
  const todayIso   = localIso(new Date());
  const metrics = [
    stats.workoutCount > 0 && { v: stats.workoutCount, l: 'אימונים' },
    stats.raceCount > 0 && { v: stats.raceCount, l: stats.raceCount === 1 ? 'מרוץ' : 'מרוצים' },
    stats.longRunKm && { v: stats.longRunKm, u: 'ק״מ', l: 'ריצה ארוכה' },
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-4">
      {onClose && (
        <button type="button" onClick={onClose} className="self-start text-text-muted text-xl leading-none">›</button>
      )}

      {tabs}

      {/* ── Week summary card ─────────────────────────────────────────────────────────── */}
      <section className="rounded-[16px] bg-surface border border-border p-4">
        {(dateRange || (daysToRace > 0 && raceName)) && (
          <div className="flex items-baseline justify-between gap-2 flex-wrap text-[13px] text-text-muted">
            {dateRange && <span dir="ltr">{dateRange}</span>}
            {daysToRace > 0 && raceName && <span>{raceName} · בעוד {daysToRace} ימים</span>}
          </div>
        )}
        {totalKm != null && (
          <div className="flex items-baseline gap-1.5 mt-2">
            <span className="num-display text-text" style={{ fontSize: 56, lineHeight: 0.9 }}>{totalKm}</span>
            <span className="text-[14px] text-text-body">ק״מ השבוע</span>
          </div>
        )}
        {metrics.length > 0 && (
          <div className="flex mt-4 pt-3 border-t border-border">
            {metrics.map((m, i) => (
              <div key={m.l} className={`flex-1 min-w-0 px-3 ${i ? 'border-s border-border' : 'ps-0'}`}>
                <div className="num-display text-[26px] text-text tabular-nums" style={{ lineHeight: 1 }}>
                  {m.v}{m.u && <span className="font-sans font-normal text-[13px] text-text-body ms-1">{m.u}</span>}
                </div>
                <div className="text-[12px] text-text-muted mt-1">{m.l}</div>
              </div>
            ))}
          </div>
        )}

        {/* Coach note — advice, not an alarm */}
        {plan.load_warning && (
          <div className="mt-4 pt-3 border-t border-border">
            <div className="font-bold text-[12px] text-accent mb-1">המאמן</div>
            <p className="m-0 text-[15px] leading-relaxed text-text">{plan.load_warning}</p>
          </div>
        )}
      </section>

      {/* ── Day list — tap a workout row to expand DayCard inline ─────────────────────── */}
      <div>
        <h2 className="font-semibold text-[13px] text-text-muted mb-2">הימים השבוע</h2>
        <div className="flex flex-col gap-2">
          {plan.days.map((day, dayIdx) => {
            const isOpen = expandedDay?.day_name === day.day_name;
            // dd.mm per day from week_start + position (every row carries a date).
            let dayDate = null;
            let iso = day.date || null;
            try {
              if (plan.week_start) {
                const d0 = new Date(`${plan.week_start}T00:00:00`);
                d0.setDate(d0.getDate() + dayIdx);
                dayDate = `${String(d0.getDate()).padStart(2, '0')}.${String(d0.getMonth() + 1).padStart(2, '0')}`;
                iso = iso || localIso(d0);
              }
            } catch { /* date stays null */ }
            return (
              <React.Fragment key={day.day_name}>
                <DayRow
                  day={day}
                  dayDate={dayDate}
                  isToday={iso === todayIso}
                  isOpen={isOpen}
                  onExpand={d => setExpandedDay(isOpen ? null : d)}
                />
                {isOpen && (
                  <div className="rounded-[16px] overflow-hidden border border-border -mt-0.5">
                    <DayCard day={day} compact={false} sportType={plan.sport_type || 'running'} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Tips + gear (§5.2) ─────────────────────────────────────────────────────────── */}
      <TipsList tips={plan.tips_summary} gearNotes={plan.gear_notes} />
    </div>
  );
}
